import React from 'react';
import {
  CheckCircle,
  SnackbarCancel,
  BlueSearch,
  VoiceAndToneCheckMark
} from 'src/components/SvgIcons';
import { GenerationStatus, GenerationStatusEnum, ContentGenerationRecord } from '../types';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import CircularProgress from '@mui/material/CircularProgress';
import { circularProgressClasses } from '@mui/material';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import { useContentGenReviewStore } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/stores';
import styled from '@emotion/styled';
import colors from 'src/utils/colors';
import { BEACON_SUBTABS, BEACON_TABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';

const StyledCircularProgress = styled(CircularProgress)<{
  progressColor?: string;
  trackColor?: string;
}>`
  color: ${(props) => props.progressColor || colors.darkBlue};
  position: relative;

  & .${circularProgressClasses.circle} {
    stroke-linecap: round;
    stroke-width: 4px;
  }

  &::before {
    content: '';
    z-index: -1;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 50%;
    border: 2px solid ${(props) => props.trackColor || colors.blueGrey};
    box-sizing: border-box;
  }
`;

const enumToLabel: Record<GenerationStatus, string> = {
  [GenerationStatusEnum.enum.NO_GENERATION]: 'Ready to generate',
  [GenerationStatusEnum.enum.GENERATING]: 'Generating',
  [GenerationStatusEnum.enum.READY_TO_REVIEW]: 'Ready to review',
  [GenerationStatusEnum.enum.REVIEWED]: 'Reviewed',
  [GenerationStatusEnum.enum.GENERATION_ERROR]: 'Generation error',
  [GenerationStatusEnum.enum.READY_TO_GENERATE]: 'Ready to generate'
};

const StatusIcon = ({ status }: { status: GenerationStatus }) => {
  switch (status) {
    case GenerationStatusEnum.enum.GENERATING:
      return <StyledCircularProgress size={16} progressColor={colors.darkBlue} trackColor={colors.blueGrey} />;

    case GenerationStatusEnum.enum.READY_TO_REVIEW:
      return <BlueSearch style={{ width: '16px', height: '16px' }} />;
    case GenerationStatusEnum.enum.REVIEWED:
      return <VoiceAndToneCheckMark style={{ width: '16px', height: '16px' }} />;
    case GenerationStatusEnum.enum.GENERATION_ERROR:
      return <SnackbarCancel style={{ width: '16px', height: '16px' }} />;
    case GenerationStatusEnum.enum.NO_GENERATION:
      return <CheckCircle style={{ width: '16px', height: '16px' }} />;
    case GenerationStatusEnum.enum.READY_TO_GENERATE:
      return <CheckCircle style={{ width: '16px', height: '16px' }} />;
    default:
      return <></>;
  }
};

export const Status = ({
  data,
  onEnableFinalReview
}: {
  data: ContentGenerationRecord;
  onEnableFinalReview?: () => void;
}) => {
  const status = data.status as GenerationStatus;

  GenerationStatusEnum.parse(status);

  const redirectToReview =
    status === GenerationStatusEnum.enum.READY_TO_REVIEW || status === GenerationStatusEnum.enum.REVIEWED;
  const { beaconNavigator } = useBeaconNavigator();
  const { setContentGenItemsToReview } = useContentGenReviewStore();

  const handleClick = () => {
    if (redirectToReview && data.stacklineSku) {
      if (onEnableFinalReview) {
        beaconNavigator.updateSearchParams({
          tab: BEACON_TABS.PROJECTS,
          subtab: BEACON_SUBTABS.PROJECTS_REVIEW,
          sku: data?.retailerSku,
          contentGenId: data.skuContentGenerationId,
          projectId: data.projectId,
          finalReviewState: 'auto'
        });
        beaconNavigator.openInNewTab();
        return;
      }

      setContentGenItemsToReview([data]);
      beaconNavigator.updateSearchParams({ tab: BEACON_TABS.GENERATION, subtab: BEACON_SUBTABS.GENERATION_REVIEW });
      beaconNavigator.updateSearchParams({ sku: data?.retailerSku });
      beaconNavigator.updateSearchParams({ contentGenId: data.skuContentGenerationId });
      beaconNavigator.go();
    }
  };

  return (
    <Flex alignItems="center" gap={12} sx={{ whiteSpace: 'nowrap', width: '144px'}}>
      <div
        style={{
          width: '16px',
          height: '16px',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}
      >
        <StatusIcon status={status} />
      </div>
      <Text
        variant="body2"
        onClick={handleClick}
        sx={{
          cursor: redirectToReview ? 'pointer' : 'default',
          textDecoration: redirectToReview ? 'underline' : 'none',
          whiteSpace: 'nowrap'
        }}
      >
        {enumToLabel[status]}
      </Text>
    </Flex>
  );
};
